import { NgClass, ViewportScroller } from '@angular/common';
import {
    Component,
    inject,
    computed,
    signal,
    WritableSignal,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { SubSeason } from '../../../../services/api.service';
import { BookingService } from '../../../../services/booking.service';
import { ButtonComponent } from '../../../common/button/button.component';
import { DividerComponent } from '../../../common/divider/divider.component';
import { DatePickerComponent } from '../date-picker/date-picker.component';
import { PersonSelectionComponent } from '../person-selection/person-selection.component';

@Component({
    selector: 'app-booking-form',
    imports: [
        DatePickerComponent,
        PersonSelectionComponent,
        DividerComponent,
        ButtonComponent,
        RouterLink,
        NgClass,
        TranslatePipe,
    ],
    templateUrl: './booking-form.component.html',
})
export class BookingFormComponent {
    bookingService = inject(BookingService);
    config = computed(() => this.bookingService.config.value());
    minRangeSpan = computed(() => this.config()?.data.minimumStay || 3);
    maximumGuests = computed(() => this.config()?.data.maximumPeople || 9);
    applicableSeason = signal<{
        subSeason: SubSeason;
        seasonName: string;
    } | null>(null);
    daysAmount = signal(0);
    viewportScroller = inject(ViewportScroller);

    handleSignalChange<T>(
        count: T,
        signal: WritableSignal<T>,
        checkSeason: boolean = false,
    ) {
        signal.set(count);

        if (checkSeason && this.bookingService.startDate()) {
            this.applicableSeason.set(
                this.bookingService.getApplicableSeason(
                    this.bookingService.startDate()!,
                    this.config()?.data.seasons,
                ),
            );
        }
    }

    formatTime(time: string | undefined): string {
        if (!time) {
            return '';
        }
        return time.split(':').slice(0, 2).join(':');
    }

    getTotalPersonAmount() {
        return (
            this.bookingService.adultAmount() +
            this.bookingService.childrenAmount() +
            this.bookingService.infantsAmount()
        );
    }

    getCleaningCosts() {
        const minimumPeopleForBasePrices =
            this.config()?.data.minimumPeopleForBasePrices || 4;

        if (this.getTotalPersonAmount() > minimumPeopleForBasePrices) {
            return (
                (this.applicableSeason()?.subSeason)!.cleaningCosts *
                this.getTotalPersonAmount()
            );
        }

        return (
            (this.applicableSeason()?.subSeason)!.cleaningCosts *
            minimumPeopleForBasePrices
        );
    }

    getPricePerNight() {
        if (!this.applicableSeason()) {
            return 0;
        }

        const minimumPeopleForBasePrices =
            this.config()?.data.minimumPeopleForBasePrices || 4;

        if (
            this.bookingService.adultAmount() +
                this.bookingService.childrenAmount() <=
            minimumPeopleForBasePrices
        ) {
            return this.applicableSeason()!.subSeason.basePrice;
        }

        let personArray = [];
        for (let i = 0; i < this.bookingService.adultAmount(); i++) {
            personArray.push(this.applicableSeason()!.subSeason.adultPrice);
        }

        for (let i = 0; i < this.bookingService.childrenAmount(); i++) {
            personArray.push(this.applicableSeason()!.subSeason.childPrice);
        }

        let personPrice = 0;
        for (let i = minimumPeopleForBasePrices; i < personArray.length; i++) {
            personPrice += personArray[i];
        }

        return this.applicableSeason()!.subSeason.basePrice + personPrice;
    }

    getTotalPrice() {
        return (
            this.getCleaningCosts() +
            this.getPricePerNight() * (this.daysAmount() - 1)
        );
    }
}
